// Performance monitoring utilities
export const measurePerformance = (name, fn) => {
  return async (...args) => {
    const start = performance.now();
    const result = await fn(...args);
    const end = performance.now();
    console.log(`${name} took ${end - start} milliseconds`);
    return result;
  };
};

// Enhanced Web Vitals reporting with mobile-specific tracking
export const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB, getINP }) => {
      // Core Web Vitals
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);

      // New metric for mobile interaction
      if (getINP) {
        getINP(onPerfEntry);
      }

      // Track mobile-specific metrics
      trackMobileMetrics(onPerfEntry);
    });
  }
};

// Mobile-specific performance tracking
export const trackMobileMetrics = (onPerfEntry) => {
  // Track connection type for mobile optimization
  if ('connection' in navigator) {
    const connection = navigator.connection;
    onPerfEntry({
      name: 'connection-type',
      value: connection.effectiveType,
      rating: connection.effectiveType === '4g' ? 'good' : 'needs-improvement',
      delta: 0,
      id: 'connection-' + Date.now()
    });
  }

  // Track device memory if available
  if ('deviceMemory' in navigator) {
    onPerfEntry({
      name: 'device-memory',
      value: navigator.deviceMemory,
      rating: navigator.deviceMemory >= 4 ? 'good' : 'needs-improvement',
      delta: 0,
      id: 'memory-' + Date.now()
    });
  }

  // Track viewport size for mobile optimization
  onPerfEntry({
    name: 'viewport-width',
    value: window.innerWidth,
    rating: window.innerWidth >= 768 ? 'good' : 'mobile',
    delta: 0,
    id: 'viewport-' + Date.now()
  });
};

// Image loading optimization
export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

// Batch image preloading
export const preloadImages = (srcArray) => {
  return Promise.all(srcArray.map(preloadImage));
};

// Debounce utility for performance
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle utility for performance
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};
